// import type { Breadcrumb } from "@sentry/types";
import { enableSentryTracing, Sentry as HttpHelpersSentry } from "@toruslabs/http-helpers";
import log from "loglevel";
import { App } from "vue";
import { Router } from "vue-router";

import appConfig from "../config";
import { isUnauthorizedError } from "./utils";

export async function installSentry(app: App, router: Router): Promise<void> {
  if (!import.meta.env.VITE_SENTRY_DSN) return;
  const [Sentry, loglevelSentryModule] = await Promise.all([import("@sentry/vue"), import("@toruslabs/loglevel-sentry")]);

  const { LoglevelSentry } = loglevelSentryModule;

  Sentry.init({
    app,
    dsn: import.meta.env.VITE_SENTRY_DSN,
    environment: appConfig.buildEnv,
    release: `wallet-services@${appConfig.appVersion}`,
    integrations: [
      Sentry.browserTracingIntegration({
        router,
      }),
    ],
    tracePropagationTargets: [appConfig.api, appConfig.commonApiHost],
    ignoreErrors: [
      // Happen when user click 'X' on the browser (ref https://forum.sentry.io/t/typeerror-failed-to-fetch-reported-over-and-overe/8447/2)
      "TypeError: Failed to fetch", // All except iOS and Firefox
      "TypeError: cancelled", // iOS
      "TypeError: NetworkError when attempting to fetch resource.", // Firefox
    ],
    sampleRate: Number.parseFloat(appConfig.sentrySampleRate),
    tracesSampleRate: Number.parseFloat(appConfig.sentryTransactionSampleRate),
    normalizeDepth: 8,
    // beforeBreadcrumb(breadcrumb: Breadcrumb) {
    // breadcrumb.data = redactBreadcrumbData(breadcrumb.data);
    // return breadcrumb;
    // },
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 0,
  });

  const plugin = new LoglevelSentry(Sentry);
  plugin.install(log);

  // https://github.com/pimterry/loglevel?tab=readme-ov-file#writing-plugins
  const originalFactory = log.methodFactory;
  log.methodFactory = (method, level, name) => {
    const rawMethod = originalFactory(method, level, name);

    if (method === "error") {
      return async (...args: unknown[]) => {
        // don't log unauthorised errors
        if (args.find(isUnauthorizedError)) return undefined;
        return rawMethod(...args);
      };
    }

    return (...args: unknown[]) => rawMethod(...args);
  };
  log.rebuild();

  enableSentryTracing(Sentry as unknown as HttpHelpersSentry, [appConfig.api, appConfig.commonApiHost], []);
}
